import React, { useState } from "react";
import SEO from "../components/SEO";
import "../styles/Apps.css";
import { useScrollReveal } from "../hooks/useScrollReveal";

const appsData = [
  {
    name: "Ecommerce Platform App",
    description: "Scalable commerce experience with secure checkout, smart search, and operational dashboards.",
    image: "/assets/app1.jpg",
    status: "Production",
    platforms: ["iOS", "Android"],
    downloads: "500K+",
    rating: 4.6
  },
  {
    name: "War Gun Game",
    description: "Action-focused mobile game with retention loops, event systems, and optimized rendering performance.",
    image: "/assets/game1.jpg",
    status: "LiveOps",
    platforms: ["iOS", "Android"],
    downloads: "2M+",
    rating: 4.8
  },
  {
    name: "Fitness Tracker Pro",
    description: "Comprehensive health monitoring app with workout plans, nutrition tracking, and progress analytics.",
    image: "/assets/fitness.jpg",
    status: "Production",
    platforms: ["iOS", "Android", "Web"],
    downloads: "1M+",
    rating: 4.7
  },
  {
    name: "Social Connect",
    description: "Community-building platform with real-time messaging, content sharing, and engagement features.",
    image: "/assets/social.jpg",
    status: "Growth",
    platforms: ["iOS", "Android"],
    downloads: "3M+",
    rating: 4.5
  },
  {
    name: "AR Navigation",
    description: "Augmented reality navigation app providing immersive wayfinding experiences for indoor and outdoor locations.",
    image: "/assets/ar-nav.jpg",
    status: "Beta",
    platforms: ["iOS", "Android"],
    downloads: "100K+",
    rating: 4.9
  },
  {
    name: "Crypto Wallet",
    description: "Secure cryptocurrency wallet with multi-chain support, staking features, and DeFi integration.",
    image: "/assets/crypto.jpg",
    status: "Production",
    platforms: ["iOS", "Android", "Web"],
    downloads: "750K+",
    rating: 4.4
  }
];

const capabilities = [
  "Mobile App Development",
  "Game Development",
  "Product Architecture",
  "LiveOps & Growth",
  "Monetization Strategy",
  "Store Release Management",
  "UI/UX Design",
  "Backend Development",
  "API Integration",
  "Cloud Infrastructure"
];

const deliveryFlow = [
  "Discovery and scope definition",
  "UX mapping and technical design",
  "Agile implementation with QA gates",
  "Launch, analytics setup, and optimization",
];

const appStats = [
  { label: "Apps Launched", value: "50+" },
  { label: "Total Downloads", value: "15M+" },
  { label: "Average Rating", value: "4.7" },
  { label: "Countries Reached", value: "30+" }
];

function Apps() {
  useScrollReveal();
  const [filter, setFilter] = useState("All");
  
  const categories = ["All", "Games", "Productivity", "Health & Fitness", "Social", "Finance"];
  
  const filteredApps = filter === "All" 
    ? appsData 
    : appsData.filter(app => {
        if (filter === "Games") return app.name.includes("Game");
        if (filter === "Productivity") return app.name.includes("Ecommerce") || app.name.includes("Navigation");
        if (filter === "Health & Fitness") return app.name.includes("Fitness");
        if (filter === "Social") return app.name.includes("Social");
        if (filter === "Finance") return app.name.includes("Crypto");
        return true;
      });

  return (
    <div className="apps">
      <SEO title="Apps" description="Explore MacroMatrix's mobile apps and games — from hyper-casual hits to enterprise solutions." path="/apps" />
      <section className="apps-hero">
        <div className="hero-bg-pattern" />
        <div className="hero-content">
          <span className="hero-badge">Our Portfolio</span>
          <h1>Apps & Products</h1>
          <p>We build and operate mobile products with a focus on performance, retention, and sustainable growth across multiple industries.</p>
          <div className="app-stats-grid">
            {appStats.map((stat, index) => (
              <div key={index} className="stat-card">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="capabilities reveal">
        <h2>Core Capabilities</h2>
        <div className="capability-list">
          {capabilities.map((item) => (
            <span key={item} className="capability-chip">
              {item}
            </span>
          ))}
        </div>
      </section>

      <section className="app-filter-section reveal">
        <h2>Browse by Category</h2>
        <div className="filter-buttons">
          {categories.map(category => (
            <button
              key={category}
              className={`filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      <section className="app-list-wrap reveal">
        <h2>Featured Work</h2>
        <div className="app-list">
          {filteredApps.map((app, index) => (
            <article className="app-item" key={app.name} style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="app-image-wrapper">
                <img src={app.image} alt={app.name} loading="lazy" />
                <div className="app-overlay">
                  <div className="app-platforms">
                    {app.platforms.map(platform => (
                      <span key={platform} className="platform-tag">{platform}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="app-info">
                <div className="app-header">
                  <h3>{app.name}</h3>
                  <span className={`app-status status-${app.status.toLowerCase()}`}>{app.status}</span>
                </div>
                <p>{app.description}</p>
                <div className="app-metrics">
                  <div className="metric">
                    <span className="metric-icon">📥</span>
                    <span>{app.downloads}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-icon">⭐</span>
                    <span>{app.rating}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="delivery-flow reveal">
        <h2>Delivery Framework</h2>
        <p className="flow-subtitle">Our proven process ensures quality and timely delivery</p>
        <div className="flow-list">
          {deliveryFlow.map((step, index) => (
            <article className="flow-item" key={step}>
              <span className="flow-number">0{index + 1}</span>
              <p>{step}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="apps-cta reveal">
        <div className="cta-content">
          <h2>Ready to Build Your App?</h2>
          <p>Let's transform your idea into a successful digital product. Our team is ready to help you every step of the way.</p>
          <div className="cta-buttons">
            <a href="/contact" className="btn-primary">Start a Project</a>
            <a href="/services" className="btn-secondary">Explore Services</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Apps;
